import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Zap, ArrowRight, CheckCircle2, Crown, Star, Flame, Activity, Gauge, Rocket } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const iconPalette = [Zap, Flame, Activity, Gauge, Rocket, Crown, Star];
const packagesEndpoint = "https://aerovolt-db464-default-rtdb.firebaseio.com/Packages.json";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  if (Number.isFinite(num)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(num);
  }
  return String(value);
};

const formatRange = (minInvestment, maxInvestment) => {
  const minText = formatCurrency(minInvestment);
  const maxText = maxInvestment === "Unlimited" ? "Unlimited" : formatCurrency(maxInvestment);
  if (maxText === "N/A") return minText;
  return `${minText} - ${maxText}`;
};

const formatDuration = (durationDays) => {
  if (durationDays === null || durationDays === undefined || durationDays === "") return "N/A";
  if (Number.isFinite(Number(durationDays))) {
    return `${Number(durationDays)} days`;
  }
  return String(durationDays);
};

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError("");
        
        console.log("Fetching packages from:", packagesEndpoint);
        
        // Try to fetch from Firebase
        const response = await fetch(packagesEndpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          cache: 'no-cache',
        });
        
        console.log("Response status:", response.status);
        
        if (!response.ok) {
          throw new Error(`Failed to load packages (${response.status})`);
        }
        
        const data = await response.json();
        console.log("Raw Firebase data:", data);
        
        if (!isMounted) return;

        // Check if data exists and is an object
        if (!data || typeof data !== 'object') {
          setPackages([]);
          setLoading(false);
          return;
        }

        // Convert the object to array and map each package
        const loaded = Object.entries(data).map(([id, pkg], index) => {
          console.log(`Processing package ${id}:`, pkg);
          
          // Handle referral levels correctly
          let referralLevels = [];
          if (pkg.referralLevels) {
            // If referralLevels is an object, convert to array
            if (typeof pkg.referralLevels === 'object' && !Array.isArray(pkg.referralLevels)) {
              referralLevels = Object.values(pkg.referralLevels);
            } else if (Array.isArray(pkg.referralLevels)) {
              referralLevels = pkg.referralLevels;
            }
          }

          // Get description from Firebase - this is the key change
          // If description exists in Firebase, use it, otherwise use a default
          const packageDescription = pkg.description || pkg.name || `Investment package with ${pkg.dailyRoi || 'competitive'}% daily returns`;

          return {
            id,
            ...pkg,
            // Ensure numeric values are properly parsed
            minInvestment: pkg.minInvestment ? Number(pkg.minInvestment) : 0,
            maxInvestment: pkg.maxInvestment === "Unlimited" ? "Unlimited" : (pkg.maxInvestment ? Number(pkg.maxInvestment) : "Unlimited"),
            dailyRoi: pkg.dailyRoi ? Number(pkg.dailyRoi) : 0,
            durationDays: pkg.durationDays ? Number(pkg.durationDays) : 0,
            referralLevels: referralLevels,
            // Set icon based on index
            icon: iconPalette[index % iconPalette.length],
            // Use the description from Firebase
            description: packageDescription,
            // Ensure workingDays exists
            workingDays: pkg.workingDays || "Mon-Fri",
            // Keep the name for display
            displayName: pkg.name || id.replace('_', ' ')
          };
        });

        console.log("Processed packages:", loaded);

        // Sort packages by minInvestment
        loaded.sort((a, b) => {
          const aMin = Number(a.minInvestment) || 0;
          const bMin = Number(b.minInvestment) || 0;
          return aMin - bMin;
        });

        setPackages(loaded);
      } catch (err) {
        console.error("Error fetching packages:", err);
        if (!isMounted) return;
        setError(err.message || "Failed to load packages. Please try again later.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPackages();
    return () => {
      isMounted = false;
    };
  }, []);

  const hasPackages = useMemo(() => packages.length > 0, [packages]);

  return (
    <div>
      <PageHero
        eyebrow="Energy Plans"
        title={<>Energy <span className="energy-gradient-text">Participation Plans</span></>}
        description="Choose a participation plan that aligns with your investment goals. Every plan connects you to real, operational renewable energy infrastructure generating electricity for national power grids."
        backgroundImage={heroBg}
      />

      {/* Packages */}
      <Section>
        <SectionHeading
          eyebrow="Choose Your Plan"
          title="Investment Packages"
          description="Each plan provides access to our portfolio of wind and solar assets. Distributions are performance-based - calculated from actual energy revenue."
        />

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            <p className="mt-4 text-sm text-muted-foreground">Loading packages from energy grid...</p>
          </div>
        )}
        
        {!loading && error && (
          <div className="text-center py-12">
            <div className="text-red-400 mb-2">⚠️ {error}</div>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 mt-4 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        )}
        
        {!loading && !error && !hasPackages && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No investment packages are currently available. Please check back later.</p>
          </div>
        )}

        {!loading && hasPackages && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg, i) => (
              <motion.div
                key={pkg.id || i}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="relative p-7 rounded-2xl glass-card glass-card-hover transition-all duration-500 group overflow-hidden"
              >
                {/* Top glow */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl energy-gradient flex items-center justify-center group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
                    <pkg.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold">{pkg.displayName || "Energy Package"}</h3>
                    <p className="text-xs text-muted-foreground">
                      {formatRange(pkg.minInvestment, pkg.maxInvestment)}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="text-3xl font-bold text-primary glow-text">
                    {Number.isFinite(pkg.dailyRoi) ? `${pkg.dailyRoi}%` : "N/A"}
                  </div>
                  <div className="text-xs text-muted-foreground">daily performance</div>
                </div>

                <div className="space-y-3 text-sm mb-6">
                  {[
                    ["Duration", formatDuration(pkg.durationDays)],
                    ["Distribution Cycle", pkg.workingDays || "Daily"],
                    [
                      "Direct Referral",
                      pkg.referralLevels && pkg.referralLevels.length > 0
                        ? `${pkg.referralLevels[0] || 0}%`
                        : `${pkg.level1Percent || 0}%`
                    ],
                    ["Capital Access", "Locked during term"],
                    ["Dashboard Access", "Full real-time"],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between items-center">
                      <span className="text-muted-foreground text-xs">{label}</span>
                      <span className="text-xs font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  {[
                    // This will show the description from Firebase
                    pkg.description || "Real-time energy monitoring",
                    "Weekly distributions",
                    "Transparent audit reports"
                  ].map((f, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </Section>

      {/* Referral Structure */}
      <Section className="bg-secondary/20 relative overflow-hidden">
        <EnergyParticles />
        <div className="relative z-10">
          <SectionHeading eyebrow="Referral Program" title="Build Your Energy Network" description="Earn referral bonuses across 5 levels when you invite others to participate in AeroVolt energy projects." />
          <div className="max-w-2xl mx-auto">
            <div className="space-y-3">
              {[
                { level: "Level 1", pct: "9%", desc: "Direct referral bonus on partner participation" },
                { level: "Level 2", pct: "7%", desc: "Second-generation referral bonus" },
                { level: "Level 3", pct: "4%", desc: "Third-generation referral bonus" },
                { level: "Level 4", pct: "2%", desc: "Fourth-generation referral bonus" },
                { level: "Level 5", pct: "1%", desc: "Fifth-generation referral bonus" },
              ].map((l, i) => (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="flex items-center gap-4 p-4 rounded-xl glass-card glass-card-hover transition-all duration-500">
                  <div className="font-display text-2xl font-bold text-primary glow-text w-16 text-center">{l.pct}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-semibold">{l.level}</div>
                    <div className="text-xs text-muted-foreground">{l.desc}</div>
                  </div>
                  <div className="w-full max-w-[120px] h-2 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      className="h-full energy-gradient rounded-full"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${[90, 70, 40, 20, 10][i]}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15, duration: 0.8 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Packages;